import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../../contexts/AuthContext';
import NivoChartRenderer from '../ChartDisplay/NivoChartRenderer';
import DeleteWidgetModal from './DeleteWidgetModal';
import { refreshWidgetData, updateWidget } from '../../services/dashboardApi';
import { transformChartData } from '../../utils/chartHelpers';
import { logger } from '../../utils/logger';
import './DashboardWidget.css';
import '../ChartDisplay/ChartTypeSelector.css';

/**
 * Individual widget component for dashboard
 * @param {Object} props - Component props
 * @param {Object} props.widget - Widget data with query info
 * @param {function} props.onDelete - Callback when widget is deleted
 * @param {function} props.onWidgetClick - Callback when widget chart is clicked
 * @returns {JSX.Element} Widget component
 */
const DashboardWidget = ({ widget, onDelete, onWidgetClick }) => {
  const { t } = useTranslation();
  const { activeCompany } = useAuth();
  const [widgetData, setWidgetData] = useState(null);
  const [selectedChartType, setSelectedChartType] = useState(widget.default_chart);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Setup sortable functionality (draggable + droppable combined)
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: widget.id,
    data: {
      widget: widget
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    gridRow: widget.row + 1,
    gridColumn: widget.column + 1,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto'
  };

  // User-initiated load (e.g. retry button) - no abort signal
  const loadWidgetData = React.useCallback(async () => {
    if (!activeCompany?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await refreshWidgetData(widget.query_id, activeCompany.id);
      if (result.data) {
        const transformed = transformChartData(result.data);
        setWidgetData(transformed);
      } else {
        setError(t('dashboards.widgetError'));
      }
    } catch (err) {
      logger.error('Failed to load widget data:', err);
      setError(err.message || t('dashboards.widgetError'));
    } finally {
      setIsLoading(false);
    }
  }, [widget.query_id, activeCompany?.id, t]);

  // Load widget data on mount and when company or query changes (abort on company switch)
  useEffect(() => {
    if (!activeCompany?.id) return;

    const ac = new AbortController();
    const signal = ac.signal;

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await refreshWidgetData(widget.query_id, activeCompany.id, signal);
        if (signal.aborted) return;
        if (result.data) {
          const transformed = transformChartData(result.data);
          setWidgetData(transformed);
        } else {
          setError(t('dashboards.widgetError'));
        }
      } catch (err) {
        if (signal.aborted || err.name === 'AbortError') return;
        logger.error('Failed to load widget data:', err);
        setError(err.message || t('dashboards.widgetError'));
      } finally {
        if (!signal.aborted) setIsLoading(false);
      }
    };

    fetchData();
    return () => ac.abort();
  }, [widget.query_id, activeCompany?.id]);

  // Update selected chart type when widget default changes
  useEffect(() => {
    setSelectedChartType(widget.default_chart);
  }, [widget.default_chart]);

  const handleChartTypeChange = async (newChartType) => {
    const previousType = selectedChartType;
    
    // Update UI immediately for better UX
    setSelectedChartType(newChartType);
    
    try {
      // Persist to backend
      await updateWidget(
        widget.id,
        { default_chart: newChartType },
        activeCompany.id
      );
      logger.info(`Widget chart type updated to ${newChartType}`);
    } catch (err) {
      // Revert on error
      logger.error('Failed to update chart type:', err);
      setSelectedChartType(previousType);
      setError(err.message || 'Failed to update chart type');
    }
  };

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (onDelete) {
      setIsDeleting(true);
      try {
        await onDelete(widget.id, widget.name);
        // Modal will close automatically when component unmounts after deletion
        // (parent reloads dashboard and this widget no longer exists)
      } catch (err) {
        logger.error('Failed to delete widget:', err);
        setError(err.message || t('dashboards.widgetError'));
        setIsDeleting(false);
        setIsDeleteModalOpen(false);
      }
    }
  };

  const handleDeleteCancel = () => {
    if (!isDeleting) {
      setIsDeleteModalOpen(false);
    }
  };

  const handleChartClick = (e) => {
    // Only handle clicks if widget has data and is not in loading/error state
    if (widgetData && !isLoading && !error && onWidgetClick) {
      e.stopPropagation(); // Prevent any parent click handlers
      onWidgetClick({
        chartData: widgetData,
        queryId: widget.query_id,
        selectedChartType: selectedChartType
      });
    }
  };

  // Extract metadata from widget
  const factTable = widget.metadata?.fact_table;
  const dimensionalTables = widget.metadata?.dimensional_tables || [];

  return (
    <div 
      ref={setNodeRef}
      className={`dashboard-widget ${isDragging ? 'dragging' : ''}`}
      style={style}
      data-widget-id={widget.id}
      data-row={widget.row}
      data-column={widget.column}
    >
      {/* Widget Header */}
      <div className="widget-header" {...listeners} {...attributes}>
        <h4 className="widget-title">{widget.name}</h4>
        <div className="widget-header-right">
          {widgetData?.chartTypes && widgetData.chartTypes.length > 1 && (
            <div className="inline-chart-type-selector">
              <select
                value={selectedChartType || ''}
                onChange={(e) => handleChartTypeChange(e.target.value)}
                className="chart-type-select"
              >
                {widgetData.chartTypes.map((type) => (
                  <option key={type} value={type}>
                    {t(`charts.chartTypes.${type}`) || type.charAt(0).toUpperCase() + type.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          )}
          <button 
            className="widget-delete-btn"
            onClick={handleDeleteClick}
            title={t('dashboards.deleteWidget')}
            aria-label={t('dashboards.deleteWidget')}
          >
            ×
          </button>
        </div>
      </div>

      {/* Metadata (Fact Table & Dimensional Tables) */}
      {(factTable || dimensionalTables.length > 0) && (
        <div className="widget-metadata">
          {factTable && (
            <span className="metadata-item">
              <strong>{t('dashboards.factTable')}</strong> {factTable}
            </span>
          )}
          {dimensionalTables.length > 0 && (
            <span className="metadata-item">
              <strong>{t('dashboards.dimensionalTables')}</strong> {dimensionalTables.join(', ')}
            </span>
          )}
        </div>
      )}

      {/* Chart Area or Loading/Error State */}
      <div 
        className={`widget-chart ${widgetData && !isLoading && !error ? 'clickable' : ''}`}
        onClick={handleChartClick}
        style={widgetData && !isLoading && !error ? { cursor: 'pointer' } : {}}
      >
        {isLoading ? (
          <div className="widget-loading">
            <div className="loading-spinner"></div>
            <p>{t('dashboards.loading')}</p>
          </div>
        ) : error ? (
          <div className="widget-error">
            <p>{error}</p>
            <button 
              className="retry-btn"
              onClick={loadWidgetData}
            >
              {t('dashboards.retry')}
            </button>
          </div>
        ) : widgetData ? (
          <NivoChartRenderer
            data={widgetData.data}
            chartType={selectedChartType}
            columns={widgetData.columns}
            dataType={widgetData.dataType}
            chartTypes={widgetData.chartTypes}
            seriesInfo={widgetData.seriesInfo}
            metadata={widgetData.metadata}
            columnDisplayNames={widgetData.columnDisplayNames}
            columnValueTypes={widgetData.columnValueTypes}
            chartConfig={widgetData.chartConfig}
            onChartTypeChange={handleChartTypeChange}
            hideHeader={true}
          />
        ) : (
          <div className="widget-no-data">
            <p>{t('charts.noData')}</p>
          </div>
        )}
      </div>

      {/* Prompt/Description - Below Chart */}
      {widget.prompt && (
        <div className="widget-description">
          <p>{widget.prompt}</p>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteWidgetModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        widgetName={widget.name}
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default DashboardWidget;

